'use client';

import { useCallback } from 'react';
import {
	SpotMarketConfig,
	TokenFaucet,
	BN,
	SpotMarketAccount,
	IWallet,
} from '@velocity-exchange/sdk';
import { Connection, PublicKey, LAMPORTS_PER_SOL } from '@solana/web3.js';

import { getTokenAddressForDepositAndWithdraw } from '@velocity-exchange/common';
import useVelocityClient from 'src/hooks/useVelocityClient';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { notify } from 'src/utils/notifications';

/**
 * Hook for requesting test token airdrops.
 */
export const useFaucetAirdrop = () => {
	const velocityClient = useVelocityClient();
	const connection = useVelocityStore((s) => s.connection.current);
	const wallet = useVelocityStore((s) => s.wallet.current.adapter);

	const tryFaucetAirdrop = useCallback(
		async (spotMarket: SpotMarketConfig) => {
			if (!velocityClient || !connection || !wallet?.publicKey) {
				console.error('Missing required dependencies for airdrop');
				return;
			}

			const notificationId = `airdrop${Date.now()}`;

			try {
				let airdropAmount = 10000;

				switch (spotMarket.symbol) {
					case 'SOL':
						airdropAmount = 1;
						break;
					case 'BTC':
						airdropAmount = 0.1;
						break;
					default:
						break;
				}

				notify({
					type: 'info',
					message: `Airdropping ${airdropAmount} ${spotMarket.symbol}`,
					id: notificationId,
					showUntilCancelled: true,
				});

				const isSol = spotMarket.symbol === 'SOL';

				if (isSol) {
					await connection.requestAirdrop(
						wallet.publicKey,
						airdropAmount * LAMPORTS_PER_SOL
					);
				} else {
					const faucet = new TokenFaucet(
						connection as Connection,
						wallet as IWallet,
						new PublicKey('V4v1mQiAdLz4qwckEb45WqHYceYizoib39cDBHSWfaB'),
						spotMarket.mint
					);

					const airdropAmountBn = new BN(
						airdropAmount * 10 ** spotMarket.precisionExp.toNumber()
					);

					const spotMarketAccount = velocityClient.getSpotMarketAccount(
						spotMarket.marketIndex
					);

					const expectedTokenAccountAddress =
						await getTokenAddressForDepositAndWithdraw(
							spotMarketAccount as SpotMarketAccount,
							wallet.publicKey
						);

					const tokenAccountInfo = await connection.getAccountInfo(
						expectedTokenAccountAddress
					);

					const tokenAccountExists = !!tokenAccountInfo;

					if (tokenAccountExists) {
						await faucet.mintToUser(
							expectedTokenAccountAddress,
							airdropAmountBn
						);
					} else {
						await faucet.createAssociatedTokenAccountAndMintTo(
							wallet.publicKey,
							airdropAmountBn
						);
					}
				}

				notify({
					type: 'success',
					message: `Airdropped ${airdropAmount} ${spotMarket.symbol}`,
					id: notificationId,
					updatePrevious: true,
				});
			} catch (e: any) {
				console.error(e);
				notify({
					type: 'error',
					message: `Airdrop failed`,
					description:
						'Please try again. If this problem persists contact the Velocity team',
					id: notificationId,
					updatePrevious: true,
				});
			}
		},
		[velocityClient, connection, wallet]
	);

	return { tryFaucetAirdrop };
};
